import "i18next";
import { useTranslation } from "react-i18next";
import React, {
  useEffect,
  useState,
  useCallback,
  useReducer,
  useRef,
  Suspense,
  lazy,
} from "react";
import PropTypes from "prop-types";
import SnackBar from "components/SnackBar/SnackBar";
import BackdropComponent from "components/Spinner/Spinner";
import { useNavigate, useLocation } from "react-router-dom";
import {
  getAllRosterStatus,
  getClassRosterWithStatus,
  searchClassRosterDetails,
  deleteRoaster,
  updateRosterRequisitionMap,
  RemoveRosterRequisitionMap,
  searchEmployeeProfile,
} from "services/userServices";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  IconButton,
  ListItem,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
  InputAdornment,
  Popper,
  Checkbox,
} from "@mui/material";
import { GridSearchIcon } from "@mui/x-data-grid";
import { styled } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowCircleRightOutlinedIcon from "@mui/icons-material/ArrowCircleRightOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import SearchOffOutlinedIcon from "@mui/icons-material/SearchOffOutlined";
import DialogBox from "components/ConfirmationDialog/DialogBox";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { KeyboardBackspace } from "@mui/icons-material";
import RosterDetailCard from "./RosterDetailCard";
import {
  SubHeading,
  StyledChip,
  LimitChip,
  AutocompleteWrapper,
  statusColorMap,
  sortOptions,
} from "./searchRostersConstants";

// Code-split: these dialogs are only needed once the user opens them, so
// they don't need to be in the initial SearchRosters bundle.
const AddRequisition = lazy(() => import("./AddRequisition"));
const FileAttachment = lazy(() => import("./FileAttachment"));
const ClassRosterHistory = lazy(() => import("./ClassRosterHistory"));

// Stable across renders: the stringify fn doesn't depend on component state.
const filterOptions = createFilterOptions({
  stringify: (option) =>
    [
      option?.ClassRosterId,
      option?.ClassRequestId,
      option?.Wave,
      option?.Trainer,
      option?.DataNitivClassReference,
    ].join(" "),
});

const CustomButton = styled(Button)(() => ({
  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
  fontSize: "12px",
  letterSpacing: "0.10em",
  borderRadius: "8px",
  fontWeight: "600",
  textTransform: "none",
  color: "white",
  backgroundColor: "#3299FF",
  "&:hover": {
    backgroundColor: "#2878CC",
  },
}));
const CustomOutlinedButton = styled(Button)(() => ({
  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
  fontSize: "12px",
  letterSpacing: "0.10em",
  borderRadius: "8px",
  fontWeight: "600",
  textTransform: "none",
  borderColor: "#3299FF",
  color: "#3299FF",
  "&:hover": {
    borderColor: "#3299FF",
    backgroundColor: "#E0F2FF",
  },
}));

// Consolidated dialog state: title/message/action/type/bulletList/isConfirmation
// always change together, so one reducer replaces 7 separate useState calls.
const initialDialogState = {
  open: false,
  action: null,
  title: "",
  message: "",
  messageType: "Confirmation",
  bulletList: [],
  isConfirmation: true,
};

function dialogReducer(state, action) {
  switch (action.type) {
    case "OPEN":
      return { ...state, open: true, ...action.payload };
    default:
      return state;
  }
}

export default function SearchRosters({ setRosterCount }) {
  const { t } = useTranslation(["SearchRosters"], {
    useSuspense: false,
  });
  const location = useLocation();
  const [expandedGroups, setExpandedGroups] = useState({
    "Recently Modified by You": true,
    "Other Rosters": false,
  });

  const hasFetched = useRef(false);

  const queryParams = new URLSearchParams(location.search);
  const classRequestId = queryParams.get("classRequestId");
  const classRosterId = queryParams.get("classRosterId");
  const fetchStatus = queryParams.get("classRosterId") ? "Lookup" : "Search";
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const snackReset = () => {
    setSnack({
      open: false,
      message: t("alert."),
      severity: "",
    });
  };
  const navigate = useNavigate();
  const [isCancelCloasedChecked, setIsCancelCloasedChecked] = useState(true);
  const [rosterStatusList, setRosterStatusList] = useState([]);
  const [selectedRosterStatusIds, setSelectedRosterStatusIds] = useState([1]);
  const [rosterList, setRosterList] = useState([]);
  const [filteredRosterList, setFilteredRosterList] = useState([]);
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [selectedRosterId, setSelectedRosterId] = useState(null);
  const [rosterDetails, setRosterDetails] = useState([]);
  const [filteredRosterDetails, setFilteredRosterDetails] = useState([]);
  const [expandedRequisitionIndex, setExpandedRequisitionIndex] =
    useState(null);
  const [expandedProjectIndex, setExpandedProjectIndex] = useState(null);
  const [expandedViewTraineeIndex, setExpandedViewTraineeIndex] =
    useState(null);
  const [selectedSortOption, setSelectedSortOption] = useState("1");
  const [dialogState, dispatchDialog] = useReducer(
    dialogReducer,
    initialDialogState,
  );
  const {
    open: dialogOpen,
    action: dialogAction,
    title: dialogTitle,
    message: dialogMessage,
    messageType: dialogMessageType,
    bulletList: dialogBulletList,
    isConfirmation: isConfirmationDialog,
  } = dialogState;
  const [requisitionDialogOpen, setRequisitionDialogOpen] = useState(false);
  const [selectedRequisitionId, setSelectedRequisitionId] = useState("");
  const [selectedRequisitionRosterId, setSelectedRequisitionRosterId] =
    useState(null);
  const [attachmentDialogOpen, setAttachmentDialogOpen] = useState(false);
  const [selectedAttachmentRequestId, setSelectedAttachmentRequestId] =
    useState(null);
  const [selectedAttachmentRosterId, setSelectedAttachmentRosterId] =
    useState(null);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [selectedHistoryRosterId, setSelectedHistoryRosterId] = useState(null);
  const [shouldGroup, setShouldGroup] = useState(true);
  const [existingRequisitionIds, setExistingRequisitionIds] = useState([]);

  const [selectedTrainerOG, setSelectedTrainerOG] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [trainerOGOracleId, setTrainerOGOracleId] = useState(null);
  const [trainerOGName, setTrainerOGName] = useState("");
  const [searchCriteria, setSearchCriteria] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const lastModifiedDays = 15;

  // Wrapped in useCallback (stable identity) so RosterDetailCard's
  // React.memo isn't defeated by a new function reference every render.
  const handleOpen = useCallback((id) => {
    setSelectedId(id);
    setIsDialogOpen(true);
  }, []);

  const CustomPopper = (props) => (
    <Popper {...props} placement="bottom-start" />
  );
  const handleSearchInputChange = (event, newInputValue) => {
    try {
      setLoading(true);
      if (newInputValue === "") {
        setTrainerOGName("");
        setTrainerOGOracleId(null);
      }
    } catch {
      setSnack({
        open: true,
        message: t(
          "alert.an_error_occurred_while_clearing_selected_result_please_try_again_",
        ),
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearchValueChange = (value) => {
    setSearchCriteria(value);
  };

  const handleSearchIconClick = async (event, searchQuery) => {
    try {
      setLoading(true);
      if (searchQuery.length < 3) {
        setSnack({
          open: true,
          message: t(
            "alert.search_criteria_must_be_at_least_3_characters_in_length_",
          ),
          severity: "error",
        });
        setLoading(false);
        return;
      }
      const searchResult = await searchEmployeeProfile(searchQuery);
      if (searchResult.status === true) {
        let searchResultDetails;
        if (/^\d+$/.test(searchQuery) && searchQuery.length >= 4) {
          searchResultDetails = searchResult.data.map((emp) => ({
            OracleId: emp.OracleId,
            Display:
              (emp.Name ?? "") + (emp.OracleId ? ` (${emp.OracleId})` : ""),
            Name: emp.Name,
            Email: emp.Email,
          }));
        } else {
          searchResultDetails = searchResult.data.map((emp) => ({
            OracleId: emp.OracleId,
            Display: emp.Display,
            Name: emp.Name,
            Email: emp.Email,
          }));
        }
        setSearchResults(searchResultDetails);
        setSnack({
          open: true,
          message: t("alert.we_found_results_matching_the_search_criteria_"),
          severity: "success",
        });
      } else {
        setSnack({
          open: true,
          message: t("alert.no_result_found_matching_with_search_criteria_"),
          severity: "error",
        });
      }
    } catch {
      setSnack({
        open: true,
        message: t("alert.an_error_occurred_while_searching_please_try_again_"),
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectionChange = async (event, value) => {
    try {
      setLoading(true);
      if (value && value.OracleId !== undefined) {
        setTrainerOGName(value.Name);
        setTrainerOGOracleId(value.OracleId);
        if (value.OracleId.toString().length === 7) {
          await fetchRosterData(value.OracleId, value.Name, []);
        }
      } else {
        await fetchRosterData(null, "", []);
        setTrainerOGName("");
        setTrainerOGOracleId(null);
      }
      setSelectedTrainerOG(value);
      setSelectedRosterId(null);
      setSelectedRequestId(null);
      setFilteredRosterDetails([]);
      setRosterCount(0);
      setSearchCriteria("");
      setSearchResults([]);
    } catch {
      setSnack({
        open: true,
        message: t(
          "alert.an_error_occurred_while_selecting_result_please_try_again_",
        ),
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleHistoryDialogOpen = useCallback((rosterId) => {
    setHistoryDialogOpen(true);
    setSelectedHistoryRosterId(rosterId);
  }, []);
  const handleHistoryDialogClose = () => {
    setHistoryDialogOpen(false);
    setSelectedHistoryRosterId(null);
  };
  const handleAttachmentDialogOpen = useCallback((requestId, rosterId) => {
    setAttachmentDialogOpen(true);
    setSelectedAttachmentRequestId(requestId);
    setSelectedAttachmentRosterId(rosterId);
  }, []);
  const handleAttachmentDialogClose = () => {
    try {
      setLoading(true);
      setAttachmentDialogOpen(false);
      const paramClassRequestId = classRequestId || selectedRequestId;
      const paramClassRosterId = classRosterId || selectedRosterId;
      fetchClassRosterDetails(
        paramClassRequestId,
        paramClassRosterId,
        fetchStatus,
      );
      setSelectedAttachmentRequestId(null);
      setSelectedAttachmentRosterId(null);
    } catch {
      setSnack({
        open: true,
        message: t("alert.an_error_occurred_while_refreshing_roster_details_"),
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };
  const handleRequisitionDialogOpen = useCallback(
    (requisitionRosterId, existingRequisitionList) => {
      setRequisitionDialogOpen(true);
      setSelectedRequisitionRosterId(requisitionRosterId);

      const parsedIds =
        existingRequisitionList?.length > 0
          ? existingRequisitionList
              .toUpperCase()
              .split(",")
              .map((id) => id.trim().split("-")[0])
          : [];

      setExistingRequisitionIds(parsedIds);
    },
    [],
  );

  const handleRequisitionDialogClose = () => {
    setRequisitionDialogOpen(false);
    setSelectedRequisitionId("");
  };
  const handleDialogOpen = useCallback(
    (action, title, message, messageType, bulletList, isConfirmation) => {
      dispatchDialog({
        type: "OPEN",
        payload: { action, title, message, messageType, bulletList, isConfirmation },
      });
    },
    [],
  );
  // Toggles which group is expanded (accordion behavior: opening one
  // collapses the others). All group data is already fully computed in
  // filteredRosterList whenever it's built, so there's nothing to fetch or
  // recompute here - this is purely a UI expand/collapse state change.
  const toggleGroup = (group) => {
    setExpandedGroups((prev) => {
      if (prev[group]) {
        return { ...prev, [group]: false };
      }
      return Object.keys(prev).reduce((acc, key) => {
        acc[key] = key === group;
        return acc;
      }, {});
    });
  };
  const fetchRosterStatuses = useCallback(async () => {
    try {
      const rosterStatusResponse = await getAllRosterStatus();
      if (rosterStatusResponse?.message === "Success") {
        window.localStorage.removeItem("SearchRoster");
        const rosterStatusListWithCounts = rosterStatusResponse.data.map(
          (status) => ({
            ...status,
            TotalRosterCount: 0, // Initialize count to 0
          }),
        );
        setRosterStatusList(rosterStatusListWithCounts);
        if (!classRequestId) {
          setSelectedRosterStatusIds([1]);
        }
      } else {
        setSnack({
          open: true,
          message: t("alert.failed_to_fetch_roster_statuses_"),
          severity: "error",
        });
      }
    } catch {
      setSnack({
        open: true,
        message: t("error_fetching_roster_statuses"),
        severity: "error",
      });
    }
  }, [classRequestId]);
  const fetchClassRosterDetails = useCallback(
    async (paramRequestId, paramRosterId, fetchStatus) => {
      try {
        const response = await searchClassRosterDetails(
          paramRequestId,
          paramRosterId,
        );
        setExpandedRequisitionIndex(null);
        setExpandedProjectIndex(null);
        setExpandedViewTraineeIndex(null);
        setFilteredRosterDetails([]);
        setRosterCount(0);
        if (response?.message === "Success") {
          const rosterDetailsResults = response.data;
          setRosterDetails(rosterDetailsResults);
          setFilteredRosterDetails(rosterDetailsResults);
          setRosterCount(rosterDetailsResults.length);
          if (fetchStatus === "Lookup") {
            // Calculate RosterStatusId-wise counts
            const rosterStatusCounts = rosterDetailsResults.reduce(
              (acc, detail) => {
                acc[detail.RosterStatusId] =
                  (acc[detail.RosterStatusId] || 0) + 1;
                return acc;
              },
              {},
            );

            // Use functional update to ensure the latest state
            setRosterStatusList((prevRosterStatusList) => {
              const updatedRosterStatusList = prevRosterStatusList.map(
                (status) => ({
                  ...status,
                  TotalRosterCount:
                    rosterStatusCounts[status.RosterStatusId] || 0,
                }),
              );

              // Update SelectedRosterStatusIds with RosterStatusIds where TotalRosterCount > 0
              const filteredRosterStatusIds = updatedRosterStatusList
                .filter((status) => status.TotalRosterCount > 0) // Filter statuses with TotalRosterCount > 0
                .map((status) => status.RosterStatusId); // Extract RosterStatusId

              setSelectedRosterStatusIds(filteredRosterStatusIds);
              return updatedRosterStatusList; // Return the updated list
            });
          }
        } else {
          setSnack({
            open: true,
            message: t("alert.failed_to_fetch_class_roster_details_"),
            severity: "error",
          });
        }
      } catch {
        setSnack({
          open: true,
          message: t("error_fetching_class_roster_details"),
          severity: "error",
        });
      }
    },
    [setRosterCount],
  );

  const fetchRosterData = useCallback(
    async (
      trainerOGOracleId,
      trainerOGFullName,
      selectedRosterStatusList,
      last12MonthsOnlyOverride,
    ) => {
      try {
        setLoading(true);

        const filteredRosterStatusList =
          selectedRosterStatusList.length > 0
            ? selectedRosterStatusList
            : selectedRosterStatusIds;

        // Let callers (e.g. the checkbox handler) pass the just-changed
        // checkbox value explicitly, since state updates aren't synchronous
        // and the closure here may still see the previous value otherwise.
        const effectiveLast12MonthsOnly =
          last12MonthsOnlyOverride !== undefined
            ? last12MonthsOnlyOverride
            : isCancelCloasedChecked;

        // API now returns only rosters matching these statuses - and, when
        // effectiveLast12MonthsOnly is true, Closed/Cancelled rosters are
        // already restricted to the last 12 months server-side - instead of
        // the entire roster table (or full history) being filtered client-side.
        const rosterDataResponse = await getClassRosterWithStatus(
          filteredRosterStatusList,
          effectiveLast12MonthsOnly,
        );

        if (rosterDataResponse?.message === "Success") {
          const rosterData = rosterDataResponse.data;
          setRosterList(rosterData);

          if (trainerOGOracleId) {
            handleTrainerOGFilter(
              rosterData,
              trainerOGOracleId,
              trainerOGFullName,
            );
          } else {
            handleDefaultGrouping(rosterData);
          }
        } else {
          showError(t("alert.failed_to_fetch_roster_data_"));
        }
      } catch {
        showError(t("error_fetching_roster_data"));
      } finally {
        setLoading(false);
      }
    },
    [selectedRosterStatusIds, isCancelCloasedChecked],
  );

  // Helper: Handle filtering by trainer/OG Oracle ID
  const handleTrainerOGFilter = (
    filteredByStatus,
    trainerOGOracleId,
    trainerOGFullName,
  ) => {
    const assignedToYou = filterAndSortByTrainerOG(
      filteredByStatus,
      trainerOGOracleId,
    );

    showTrainerOGFeedback(assignedToYou.length);

    const newGroupName = `Roster(s) Assigned to ${trainerOGFullName || "You"}`;
    const finalList = assignedToYou.map((r) => ({
      ...r,
      group: newGroupName,
    }));

    setShouldGroup(true);
    setExpandedGroups({ [newGroupName]: true });
    setFilteredRosterList(finalList);
  };

  // Helper: Filter and sort rosters by trainer/OG ID
  const filterAndSortByTrainerOG = (rosters, trainerOGOracleId) => {
    return rosters
      .filter(
        (r) =>
          r.TrainerId?.toString() === trainerOGOracleId.toString() ||
          r.OGId?.toString() === trainerOGOracleId.toString() ||
          r.FDOId?.toString() === trainerOGOracleId.toString(),
      )
      .sort((a, b) => {
        const dateA = new Date(a.ModifiedDate || a.CreatedDate);
        const dateB = new Date(b.ModifiedDate || b.CreatedDate);
        return dateB - dateA;
      });
  };

  // Helper: Show feedback for trainer/OG filter results
  const showTrainerOGFeedback = (count) => {
    if (count > 0) {
      setSnack({
        open: true,
        message: t("alert.rosters_assigned_to_you_fetched_successfully_"),
        severity: "success",
      });
    } else {
      setSnack({
        open: true,
        message: t("alert.no_rosters_found_assigned_to_you_"),
        severity: "error",
      });
    }
  };

  // Helper: Handle default grouping (Recently Modified by You + Other Rosters)
  const handleDefaultGrouping = (filteredByStatus) => {
    const loggedInUser = localStorage.getItem("User");
    const cutoffDate = calculateCutoffDate(lastModifiedDays);

    const recentlyModifiedByYou = filterAndSortRecentlyModified(
      filteredByStatus,
      loggedInUser,
      cutoffDate,
    );

    const otherRosters = filterAndSortOtherRosters(
      filteredByStatus,
      recentlyModifiedByYou, // ← pass the already-computed list
    );

    const { finalList, groupStates } = buildGroupedRosterList(
      recentlyModifiedByYou,
      otherRosters,
    );

    setShouldGroup(recentlyModifiedByYou.length > 0);
    setExpandedGroups(groupStates.expanded);
    setFilteredRosterList(finalList);
  };

  // Helper: Calculate cutoff date for recent modifications
  const calculateCutoffDate = (days) => {
    const now = new Date();
    const cutoffDate = new Date();
    cutoffDate.setDate(now.getDate() - days);
    return cutoffDate;
  };

  // Helper: Filter and sort recently modified rosters
  const filterAndSortRecentlyModified = (rosters, loggedInUser, cutoffDate) => {
    return rosters
      .filter((r) => {
        const modifiedDate = new Date(r.ModifiedDate || r.CreatedDate);
        return (
          (r.CreatedBy === loggedInUser || r.ModifiedBy === loggedInUser) &&
          modifiedDate >= cutoffDate
        );
      })
      .sort((a, b) => {
        const dateA = new Date(a.ModifiedDate || a.CreatedDate);
        const dateB = new Date(b.ModifiedDate || b.CreatedDate);
        return dateB - dateA;
      });
  };

  // Helper: Filter and sort other rosters
  const filterAndSortOtherRosters = (rosters, recentlyModifiedByYou) => {
    const recentIds = new Set(
      recentlyModifiedByYou.map((r) => r.ClassRosterId),
    );
    return rosters
      .filter((r) => !recentIds.has(r.ClassRosterId))
      .sort((a, b) => b.ClassRosterId - a.ClassRosterId);
  };

  // Helper: Build grouped roster list with group states
  const buildGroupedRosterList = (recentlyModifiedByYou, otherRosters) => {
    const finalList = [];
    const newExpandedGroups = {};

    const hasRecentlyModified = recentlyModifiedByYou.length > 0;
    const hasOtherRosters = otherRosters.length > 0;

    if (hasRecentlyModified) {
      finalList.push(
        ...recentlyModifiedByYou.map((r) => ({
          ...r,
          group: "Recently Modified by You",
        })),
      );
      newExpandedGroups["Recently Modified by You"] = true;
      newExpandedGroups["Other Rosters"] = false;
    }

    if (hasOtherRosters) {
      finalList.push(
        ...otherRosters.map((r) => ({
          ...r,
          group: "Other Rosters",
        })),
      );

      // Expand Other Rosters only if Recently Modified has no data
      if (!hasRecentlyModified) {
        newExpandedGroups["Other Rosters"] = true;
      }
    }

    return {
      finalList,
      groupStates: {
        expanded: newExpandedGroups,
      },
    };
  };

  // Helper: Show error message
  const showError = (message) => {
    setSnack({
      open: true,
      message,
      severity: "error",
    });
  };
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      if (!classRosterId) {
        await fetchRosterStatuses();
      }
      if (classRequestId) {
        await fetchClassRosterDetails(classRequestId, classRosterId, "Lookup");
      } else {
        await fetchRosterData(null, "", [1]);
      }
    } catch {
      setSnack({
        open: true,
        message: t("error_fetching_data"),
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [
    classRequestId,
    classRosterId,
    fetchRosterStatuses,
    fetchClassRosterDetails,
    fetchRosterData,
  ]);

  useEffect(() => {
    let cancelled = false;

    const timeout = setTimeout(() => {
      if (!hasFetched.current && !cancelled) {
        localStorage.setItem("SearchRoster", window.location.pathname);
        hasFetched.current = true;

        // Call fetchData (assuming it's async)
        const loadData = async () => {
          try {
            await fetchData();
          } catch (error) {
            if (!cancelled) {
              console.error("Failed to fetch data:", error);
            }
          }
        };

        loadData();
      }
    }, 50);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [fetchData]);

  const handleValueChange = async (event, newValue) => {
    try {
      if (newValue.length === 0) {
        showError(t("alert.please_select_at_least_one_roster_status_"));
        return;
      }

      const selectedStatusIds = newValue.map((item) => item.RosterStatusId);
      setSelectedRosterStatusIds(selectedStatusIds);
      if (shouldFilterRosterDetails()) {
        handleRosterDetailsFilter(selectedStatusIds);
      } else {
        setSelectedRosterId(null);
        // Real API call scoped to the newly selected status(es), instead of
        // filtering an already-fetched full roster list client-side.
        await fetchRosterData(
          trainerOGOracleId,
          trainerOGName,
          selectedStatusIds,
        );
      }
    } catch {
      showError(t("error_processing_roster_status_change"));
    }
  };

  // Helper: Check if should filter roster details
  const shouldFilterRosterDetails = () => {
    return rosterDetails.length > 0 && classRequestId && !classRosterId;
  };

  // Helper: Handle filtering for roster details
  const handleRosterDetailsFilter = (selectedStatusIds) => {
    const filteredDetails = rosterDetails.filter((detail) =>
      selectedStatusIds.includes(detail.RosterStatusId),
    );
    setFilteredRosterDetails(filteredDetails);
    setRosterCount(filteredDetails.length);
  };

  const handleCancelClosedChecked = async (event) => {
    const checked = event.target.checked;
    setIsCancelCloasedChecked(checked);

    if (shouldFilterRosterDetails()) {
      return; // This checkbox only applies to the roster picker/list view.
    }

    setSelectedRosterId(null);
    // The checkbox now changes what the API itself returns for Closed/
    // Cancelled statuses, so re-fetch instead of re-deriving client-side.
    // Pass `checked` explicitly rather than relying on isCancelCloasedChecked
    // state, since the state update above hasn't been committed yet.
    await fetchRosterData(
      trainerOGOracleId,
      trainerOGName,
      selectedRosterStatusIds,
      checked,
    );
  };

  const handleSearchRoster = async () => {
    try {
      if (!selectedRosterId || !selectedRequestId) {
        setSnack({
          open: true,
          message: t("alert.please_select_a_valid_class_roster_"),
          severity: "error",
        });
        return;
      }
      setLoading(true);
      await fetchClassRosterDetails(
        selectedRequestId,
        selectedRosterId,
        "Search",
      );
      setSnack({
        open: true,
        message: t("alert.class_roster_details_fetched_successfully_"),
        severity: "success",
      });
    } catch {
      setSnack({
        open: true,
        message: t("error_fetching_class_roster_details"),
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };
  const handleSorting = async (sortOption) => {
    try {
      setLoading(true);
      setFilteredRosterDetails((prevDetails) => {
        const sortedDetails = [...prevDetails];
        switch (sortOption) {
          case "1":
            sortedDetails.sort((a, b) => a.ClassRosterId - b.ClassRosterId);
            break;
          case "2":
            sortedDetails.sort((a, b) => b.ClassRosterId - a.ClassRosterId);
            break;
          case "3":
            sortedDetails.sort((a, b) => a.Wave - b.Wave);
            break;
          case "4":
            sortedDetails.sort((a, b) => b.Wave - a.Wave);
            break;
          case "5":
            sortedDetails.sort(
              (a, b) => new Date(b.ProductionDate) - new Date(a.ProductionDate),
            );
            break;
          case "6":
            sortedDetails.sort(
              (a, b) => new Date(a.ProductionDate) - new Date(b.ProductionDate),
            );
            break;
          default:
            break;
        }
        return sortedDetails;
      });
    } catch {
      setSnack({
        open: true,
        message: t("error_during_sorting"),
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };
  const handleCancelRoster = useCallback(
    async (cancelRosterId) => {
    try {
      if (cancelRosterId) {
        const cancelRosterDialog = {
          title: t("cancel_roster.title"),
          message: t("cancel_roster.message"),
          messageType: "Confirmation",
          details: [
            t("cancel_roster.trainee_status_message"),
            t("cancel_roster.roster_status_message"),
          ],
          isConfirmation: true,
        };
        handleDialogOpen(
          async () => {
            try {
              setLoading(true);
              const cancelRosterPayload = {
                classRosterId: cancelRosterId,
                modifiedBy: localStorage.getItem("User"),
              };
              const paramClassRequestId = classRequestId || selectedRequestId;
              const paramClassRosterId = classRosterId || selectedRosterId;
              const deleteRosterResponse =
                await deleteRoaster(cancelRosterPayload);
              if (deleteRosterResponse?.message === "Success") {
                if (!paramClassRosterId) {
                  await fetchRosterStatuses();
                }
                if (paramClassRequestId) {
                  await fetchClassRosterDetails(
                    paramClassRequestId,
                    paramClassRosterId,
                    fetchStatus,
                  );
                } else {
                  await fetchRosterData(null, "", []);
                }
                setSnack({
                  open: true,
                  message: t("alert.roster_cancelled_successfully_"),
                  severity: "success",
                });
              } else {
                setSnack({
                  open: true,
                  message: t("alert.failed_to_cancel_roster_"),
                  severity: "error",
                });
              }
            } catch {
              setSnack({
                open: true,
                message: t("error_canceling_roster_please_try_again"),
                severity: "error",
              });
            } finally {
              setLoading(false);
            }
          },
          cancelRosterDialog.title,
          cancelRosterDialog.message,
          cancelRosterDialog.messageType,
          cancelRosterDialog.details,
          cancelRosterDialog.isConfirmation,
        );
      }
    } catch {
      setSnack({
        open: true,
        message: t("alert.an_error_occurred_while_saving_data_"),
        severity: "error",
      });
    }
    },
    [
      classRequestId,
      classRosterId,
      selectedRequestId,
      selectedRosterId,
      fetchStatus,
      t,
      handleDialogOpen,
      fetchRosterStatuses,
      fetchClassRosterDetails,
      fetchRosterData,
    ],
  );
  const handleAddRequisition = async () => {
    try {
      const validationError = validateRequisitionInput();
      if (validationError) {
        showError(validationError);
        return;
      }

      await processRequisitionAddition();
    } catch {
      showError(t("alert.an_error_occurred_while_saving_data_"));
    } finally {
      setLoading(false);
    }
  };

  // Helper: Validate requisition input
  const validateRequisitionInput = () => {
    if (!selectedRequisitionId) {
      return t("alert.please_select_a_requisition_id_");
    }

    if (selectedRequisitionId.length < 3) {
      return t("alert.requisition_id_must_be_at_least_3_characters_long_");
    }

    if (!selectedRequisitionRosterId) {
      return t("alert.invalid_class_roster_id_");
    }

    if (
      existingRequisitionIds.includes(
        selectedRequisitionId.trim().toUpperCase(),
      )
    ) {
      return t("alert.requisition_already_exists_in_this_roster_");
    }

    return null; // No validation errors
  };

  // Helper: Process requisition addition
  const processRequisitionAddition = async () => {
    setLoading(true);

    const addRequisitionPayload = {
      classRosterId: selectedRequisitionRosterId,
      requisitionId: selectedRequisitionId,
      modifiedBy: localStorage.getItem("User"),
    };

    const addRequisitionResponse = await updateRosterRequisitionMap(
      addRequisitionPayload,
    );

    if (addRequisitionResponse?.message === "Success") {
      await handleSuccessfulRequisitionAddition();
      showSuccessMessage();
    } else {
      showError(t("alert.failed_to_add_requisition_"));
    }
  };

  // Helper: Handle post-addition data refresh
  const handleSuccessfulRequisitionAddition = async () => {
    const paramClassRequestId = classRequestId || selectedRequestId;
    const paramClassRosterId = classRosterId || selectedRosterId;

    if (!paramClassRosterId) {
      await fetchRosterStatuses();
    }

    if (paramClassRequestId) {
      await fetchClassRosterDetails(
        paramClassRequestId,
        paramClassRosterId,
        fetchStatus,
      );
    } else {
      await fetchRosterData(null, "", []);
    }
  };

  // Helper: Show success message and cleanup
  const showSuccessMessage = () => {
    setSelectedRequisitionId("");
    setRequisitionDialogOpen(false);
    setSnack({
      open: true,
      message: t("alert.requisition_added_successfully_"),
      severity: "success",
    });
  };

  // Helper: Update roster item by removing requisition
  const updateRosterItemRequisitions = (item, classRosterId, reqId) => {
    const shouldUpdate =
      item.ClassRosterId === classRosterId &&
      item.RequisitionIds?.includes(reqId);

    if (!shouldUpdate) {
      return item;
    }

    const updatedRequisitionIds = removeRequisitionFromList(
      item.RequisitionIds,
      reqId,
    );

    return {
      ...item,
      RequisitionIds: updatedRequisitionIds,
      TotalRequisition: Math.max((item.TotalRequisition || 1) - 1, 0),
    };
  };

  // Helper: Remove requisition from comma-separated list
  const removeRequisitionFromList = (requisitionIds, reqId) => {
    return requisitionIds
      .split(",")
      .map((id) => id.trim())
      .filter((id) => id !== reqId.trim())
      .join(", ");
  };

  const handleDelete = useCallback(
    (reqId, classRosterId) => {
    const requisitionId = reqId.split("-")[0].trim();
    if (reqId && classRosterId) {
      const cancelRosterDialog = {
        title: t("dialog.remove_roster_title"),
        message: t("dialog.remove_roster_message"),
        messageType: "Confirmation",
        details:
          classRosterId && reqId
            ? [
                t("dialog.item_roster_id", {
                  rosterId: classRosterId,
                }),
                t("dialog.item_requisition_id", {
                  requisitionId: requisitionId,
                }),
              ]
            : [],
        isConfirmation: true,
      };
      handleDialogOpen(
        async () => {
          try {
            setLoading(true);
            const deleteRequisitionPayload = {
              classRosterId: classRosterId,
              requisitionId: requisitionId,
              modifiedBy: localStorage.getItem("User"),
            };

            const result = await RemoveRosterRequisitionMap(
              deleteRequisitionPayload,
            );
            if (result?.message === "Success") {
              setFilteredRosterDetails((prevList) =>
                prevList.map((item) =>
                  updateRosterItemRequisitions(item, classRosterId, reqId),
                ),
              );

              setSnack({
                open: true,
                message: t("alert.requisition_removed", { requisitionId }),
                severity: "success",
              });
            } else {
              setSnack({
                open: true,
                message: t("alert.requisition_remove_failed"),
                severity: "error",
              });
            }
          } catch {
            setSnack({
              open: true,
              message: t("error_requisition_cancelling_failed"),
              severity: "error",
            });
          } finally {
            setLoading(false);
          }
        },
        cancelRosterDialog.title,
        cancelRosterDialog.message,
        cancelRosterDialog.messageType,
        cancelRosterDialog.details,
        cancelRosterDialog.isConfirmation,
      );
    }
    },
    [t, handleDialogOpen],
  );

  const updateTraineeCounts = useCallback(
    (classRosterId, newTotalActive, newTotal) => {
      setFilteredRosterDetails((prev) =>
        prev.map((item) =>
          item.ClassRosterId === Number(classRosterId)
            ? {
                ...item,
                TotalActiveTrainee: newTotalActive,
                TotalTrainee: newTotal,
              }
            : item,
        ),
      );
    },
    [],
  );
  const handleInputChange = (event, value, reason) => {
    if (reason === "input") {
      if (!value.trim()) {
        // Reset expansion to defaults
        const hasRecent = filteredRosterList.some(
          (r) => r.group === "Recently Modified by You",
        );
        const hasOther = filteredRosterList.some(
          (r) => r.group === "Other Rosters",
        );

        setExpandedGroups((prev) => ({
          ...prev,
          "Recently Modified by You": hasRecent,
          "Other Rosters": !hasRecent && hasOther,
        }));

        return;
      }

      // Expand groups that contain matching roster
      const matchingGroups = new Set();

      filteredRosterList.forEach((item) => {
        const searchStr =
          `${item?.ClassRosterId} ${item?.ClassRequestId} ${item?.Wave} ${item?.Trainer} ${item?.DataNitivClassReference}`.toLowerCase();
        if (searchStr.includes(value.toLowerCase()) && item.group) {
          matchingGroups.add(item.group);
        }
      });

      // Build new expandedGroups map — all matching groups = true, others = false
      const allGroups = [
        ...new Set(filteredRosterList.map((item) => item.group)),
      ];

      const newExpandedGroups = allGroups.reduce((acc, groupName) => {
        acc[groupName] = matchingGroups.has(groupName);
        return acc;
      }, {});

      setExpandedGroups((prev) => ({ ...prev, ...newExpandedGroups }));
    }
  };
  const handleResetRosterSearch = async () => {
    setSelectedRosterStatusIds([1]);
    await fetchRosterData(null, "", [1]);
    setSelectedRosterId(null);
    setSelectedRequestId(null);
    setRosterDetails([]);
    setFilteredRosterDetails([]);
    setRosterCount(0);
    setSelectedTrainerOG(null);
    setTrainerOGName("");
    setTrainerOGOracleId(null);
    setSearchCriteria("");
    setSearchResults([]);
    setExpandedProjectIndex(null);
    setExpandedRequisitionIndex(null);
    setSelectedRequisitionId("");
    setSelectedRequisitionRosterId(null);
    setSelectedAttachmentRosterId(null);
    setSelectedAttachmentRequestId(null);
    setSelectedHistoryRosterId(null);
  };

  const refreshScreen = () => {
    window.location.href = "/home/searchrosters";
  };

  // Field & icon subcomponents (RosterIdField, ProjectField, RosterActions,
  // ActionIconsContainer, StatusIcon, etc.) now live in ./RosterDetailCard.js
  // as module-scope, memoized components instead of being redefined on every
  // render of SearchRosters.

  return (
    <>
      {loading ? <BackdropComponent /> : null}

      <DialogBox
        isOpen={dialogOpen}
        action={dialogAction}
        title={dialogTitle}
        message={dialogMessage}
        messageType={dialogMessageType || "Confirmation"}
        itemList={dialogBulletList}
        isConfirmation={isConfirmationDialog}
      />

      <Suspense fallback={null}>
        <AddRequisition
          loading={loading}
          setLoading={setLoading}
          setSnack={setSnack}
          setSelectedRequisitionId={setSelectedRequisitionId}
          requisitionDialogOpen={requisitionDialogOpen}
          handleRequisitionDialogClose={handleRequisitionDialogClose}
          handleAddRequisition={handleAddRequisition}
          existingRequisitionIds={existingRequisitionIds}
        />

        <FileAttachment
          attachmentMode="ClassRoster"
          setLoading={setLoading}
          setSnack={setSnack}
          classRequestId={selectedAttachmentRequestId}
          classRosterId={selectedAttachmentRosterId}
          attachmentDialogOpen={attachmentDialogOpen}
          handleAttachmentDialogClose={handleAttachmentDialogClose}
        />

        <ClassRosterHistory
          setLoading={setLoading}
          setSnack={setSnack}
          classRosterId={selectedHistoryRosterId}
          historyDialogOpen={historyDialogOpen}
          handleHistoryDialogClose={handleHistoryDialogClose}
        />
      </Suspense>

      <Grid container spacing={2}>
        {!classRosterId && (
          <>
            <Grid item xs={2.5}>
              <SubHeading>
                {!classRequestId
                  ? t("subheading.roster_status")
                  : t("subheading.filter_by_roster_status")}
              </SubHeading>
              <AutocompleteWrapper>
                <Autocomplete
                  disablePortal
                  multiple
                  limitTags={2}
                  size="small"
                  disableClearable
                  disableCloseOnSelect
                  options={rosterStatusList}
                  getOptionLabel={(option) => option.RosterStatus}
                  value={rosterStatusList.filter((option) =>
                    selectedRosterStatusIds.includes(option.RosterStatusId),
                  )}
                  onChange={handleValueChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={t("placeholder.select_type")}
                      variant="outlined"
                      size="small"
                    />
                  )}
                  renderOption={(props, option, { selected }) => (
                    <ListItem
                      {...props}
                      key={option.RosterStatusId}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <ListItemText primary={option.RosterStatus} />
                      {option.TotalRosterCount > 0 && (
                        <Chip
                          label={option.TotalRosterCount}
                          size="small"
                          sx={{
                            backgroundColor:
                              statusColorMap[option.RosterStatus] || "#666",
                            color: "white",
                          }}
                        />
                      )}
                      {selected && !classRequestId && (
                        <CheckCircleIcon
                          sx={{
                            color:
                              statusColorMap[option.RosterStatus] || "#666",
                            fontSize: "1.5rem",
                          }}
                        />
                      )}
                    </ListItem>
                  )}
                  renderTags={(value, getTagProps) => {
                    const limitTags = 2;
                    const more = value.length - limitTags;
                    return (
                      <>
                        {value.slice(0, limitTags).map((option, index) => (
                          <StyledChip
                            label={option.RosterStatus}
                            size="small"
                            {...getTagProps({
                              index,
                            })}
                          />
                        ))}
                        {more > 0 && (
                          <LimitChip label={`+${more}`} size="small" />
                        )}
                      </>
                    );
                  }}
                />
              </AutocompleteWrapper>
            </Grid>
            {classRequestId && (
              <Grid item xs={2.5}>
                <SubHeading>{t("subheading.sort_by")}</SubHeading>
                <Autocomplete
                  freeSolo
                  disablePortal
                  blurOnSelect
                  options={sortOptions}
                  getOptionLabel={(option) => option.label}
                  value={
                    sortOptions.find(
                      (option) => option.value === selectedSortOption,
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    const selectedOption = newValue?.value || "1";
                    setSelectedSortOption(selectedOption);
                    handleSorting(selectedOption);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={t("placeholder.sort_by")}
                      variant="outlined"
                      size="small"
                    />
                  )}
                />
              </Grid>
            )}
          </>
        )}
        {!classRequestId && (
          <>
            <Grid item xs={3.25}>
              <SubHeading>{t("subheading.search_trainer_og")}</SubHeading>
              <Autocomplete
                freeSolo
                value={selectedTrainerOG || null}
                options={searchResults}
                onInputChange={handleSearchInputChange}
                onChange={handleSelectionChange}
                PopperComponent={CustomPopper}
                getOptionLabel={(option) => option.Display || ""}
                renderInput={(params) => (
                  <TextField
                    name="searchTrainerOG"
                    {...params}
                    size="small"
                    placeholder={t(
                      "placeholder.search_by_oracle_id_or_trainee_name_or_email_address",
                    )}
                    onChange={(event) =>
                      handleSearchValueChange(event.target.value)
                    }
                    onKeyPress={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        handleSearchIconClick(event, searchCriteria);
                      }
                    }}
                    sx={{
                      minWidth: 120,
                    }}
                    InputProps={{
                      maxLength: 50,
                      ...params.InputProps,
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip
                            title={
                              <Typography
                                variant="body2"
                                sx={{ fontSize: "1rem" }}
                              >
                                {t("title.search")}
                              </Typography>
                            }
                          >
                            <IconButton
                              id="icon-button-search"
                              type="button"
                              onClick={(event) =>
                                handleSearchIconClick(event, searchCriteria)
                              }
                            >
                              <GridSearchIcon />
                            </IconButton>
                          </Tooltip>
                          {params.InputProps.endAdornment}
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid
              item
              xs={0.25}
              sx={{
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "center",
                paddingBottom: 0.25,
              }}
            >
              <ArrowCircleRightOutlinedIcon
                sx={{
                  color: "#3299FF",
                  height: 32,
                  width: 32,
                }}
              />
            </Grid>
            <Grid item xs={3.5}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <SubHeading sx={{ whiteSpace: "nowrap", flexShrink: 0 }}>
                  {t("subheading.class_roster")}
                </SubHeading>
                <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
                  <Checkbox
                    checked={isCancelCloasedChecked}
                    onChange={handleCancelClosedChecked}
                    sx={{ p: 0, mr: 0.5 }}
                  />
                  <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
                    {t("subheading.last_12_months")}
                  </Typography>
                </Box>
              </Box>
              <Autocomplete
                disablePortal
                size="small"
                disableClearable
                filterOptions={filterOptions}
                onInputChange={handleInputChange}
                options={filteredRosterList}
                groupBy={shouldGroup ? (option) => option.group : undefined}
                getOptionLabel={(option) =>
                  `${option.ClassRosterId} | ${option.ClassRequestId} | ${option.Wave} | ${option.DataNitivClassReference || "NA"}`
                }
                value={
                  selectedRosterId
                    ? filteredRosterList.find(
                        (roster) => roster.ClassRosterId === selectedRosterId,
                      )
                    : null
                }
                onChange={(event, newValue) => {
                  setSelectedRosterId(newValue ? newValue.ClassRosterId : null);
                  setSelectedRequestId(
                    newValue ? newValue.ClassRequestId : null,
                  );
                }}
                renderInput={(params) => {
                  const selectedRoster = selectedRosterId
                    ? filteredRosterList.find(
                        (roster) => roster.ClassRosterId === selectedRosterId,
                      )
                    : null;
                  const rosterTooltip = selectedRoster
                    ? `${selectedRoster.ClassRosterId} | ${selectedRoster.ClassRequestId} | ${selectedRoster.Wave} | ${selectedRoster.DataNitivClassReference || "NA"}`
                    : "";
                  return (
                    <Tooltip
                      title={
                        rosterTooltip ? (
                          <Typography variant="body2" sx={{ fontSize: "1rem" }}>
                            {rosterTooltip}
                          </Typography>
                        ) : (
                          ""
                        )
                      }
                    >
                      <TextField
                        {...params}
                        placeholder={t(
                          "typography._pattern_class_roster_id_class_request_id_wave_trainer_",
                        )}
                        variant="outlined"
                        size="small"
                      />
                    </Tooltip>
                  );
                }}
                renderOption={(props, option) => {
                  if (option.isGroupPlaceholder) return null;
                  return (
                    <ListItem
                      {...props}
                      key={option.ClassRosterId}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "65px 65px 75px 110px 150px",
                          width: "100%",
                          alignItems: "center",
                          borderCollapse: "collapse",
                        }}
                      >
                        <Box
                          sx={{
                            textAlign: "right",
                            borderRight: "1px solid #ccc",
                            pr: 1,
                          }}
                        >
                          <Typography fontVariantNumeric="tabular-nums">
                            {Number(option.ClassRosterId)}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            textAlign: "right",
                            borderRight: "1px solid #ccc",
                            pr: 1,
                          }}
                        >
                          <Typography fontVariantNumeric="tabular-nums">
                            {Number(option.ClassRequestId)}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            textAlign: "right",
                            borderRight: "1px solid #ccc",
                            pr: 1,
                          }}
                        >
                          <Typography fontVariantNumeric="tabular-nums">
                            {option.Wave.trim()}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            textAlign: "right",
                            borderRight: "1px solid #ccc",
                            pr: 1,
                          }}
                        >
                          <Typography fontVariantNumeric="tabular-nums">
                            {option.DataNitivClassReference || "NA"}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            textAlign: "left",
                            pl: 1,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color:
                                statusColorMap[option.RosterStatus] || "#666",
                              fontSize: "1rem",
                            }}
                          >
                            {option.RosterStatus}
                          </Typography>
                        </Box>
                      </Box>
                    </ListItem>
                  );
                }}
                renderGroup={
                  shouldGroup
                    ? (params) => {
                        const groupName = params.group;
                        const isExpanded = expandedGroups[groupName];
                        const isEmpty = !filteredRosterList.some(
                          (item) => item.group === groupName,
                        );

                        return (
                          <li key={groupName}>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                px: 2,
                                py: 1,
                                cursor: "pointer",
                                backgroundColor: "#F0F8FF",
                                borderBottom: "1px solid #d0e7ff",
                                borderTop: "1px solid #d0e7ff",
                              }}
                              onClick={() => toggleGroup(groupName)}
                            >
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontSize: "1rem",
                                  color: "#3299FF",
                                }}
                              >
                                {groupName}
                              </Typography>
                              {!isEmpty && (
                                <Typography
                                  variant="body2"
                                  sx={{ color: "#3299FF", fontSize: "1rem" }}
                                >
                                  {isExpanded ? "▲" : "▼"}
                                </Typography>
                              )}
                            </Box>
                            {isExpanded && !isEmpty && params.children}
                          </li>
                        );
                      }
                    : undefined
                }
              />
            </Grid>
            <Grid
              item
              xs={2.5}
              sx={{
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "flex-start",
                paddingBottom: 1,
                gap: 1,
              }}
            >
              <CustomButton
                variant="contained"
                onClick={handleSearchRoster}
                startIcon={<SearchOutlinedIcon />}
              >
                {t("custombutton.search")}
              </CustomButton>
              {!trainerOGOracleId && (
                <CustomOutlinedButton
                  variant="outlined"
                  onClick={handleResetRosterSearch}
                  startIcon={<SearchOffOutlinedIcon />}
                >
                  {t("customoutlinedbutton.reset")}
                </CustomOutlinedButton>
              )}
            </Grid>
          </>
        )}
        {classRosterId && (
          <>
            <Grid item xs={10}></Grid>
            <Grid item xs={2}>
              <CustomOutlinedButton
                variant="outlined"
                onClick={refreshScreen}
                startIcon={<KeyboardBackspace />}
              >
                {"Search Rosters"}
              </CustomOutlinedButton>
            </Grid>
          </>
        )}
        {filteredRosterDetails.length > 0 ? (
          <>
            <Grid item xs={12}>
              <Divider
                sx={{
                  my: 1,
                }}
              />
            </Grid>
            <Grid
              item
              xs={12}
              sx={{
                overflowY: "auto",
                maxHeight: "calc(100vh - 300px)",
              }}
            >
              {filteredRosterDetails.map((data, index) => (
                <RosterDetailCard
                  key={`${data.ClassRosterId}-${index}`}
                  data={data}
                  index={index}
                  expandedProjectIndex={expandedProjectIndex}
                  setExpandedProjectIndex={setExpandedProjectIndex}
                  expandedViewTraineeIndex={expandedViewTraineeIndex}
                  setExpandedViewTraineeIndex={setExpandedViewTraineeIndex}
                  expandedRequisitionIndex={expandedRequisitionIndex}
                  setExpandedRequisitionIndex={setExpandedRequisitionIndex}
                  selectedId={selectedId}
                  isDialogOpen={isDialogOpen}
                  setIsDialogOpen={setIsDialogOpen}
                  handleRequisitionDialogOpen={handleRequisitionDialogOpen}
                  handleAttachmentDialogOpen={handleAttachmentDialogOpen}
                  handleHistoryDialogOpen={handleHistoryDialogOpen}
                  handleCancelRoster={handleCancelRoster}
                  handleOpen={handleOpen}
                  handleDelete={handleDelete}
                  updateTraineeCounts={updateTraineeCounts}
                  navigate={navigate}
                  t={t}
                />
              ))}
            </Grid>
          </>
        ) : (
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "calc(100vh - 425px)",
                width: "100%",
                textAlign: "center",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: "#3F5765",
                  fontWeight: "bold",
                  letterSpacing: "0.10em",
                  padding: 2,
                }}
              >
                {t("typography.no_class_rosters_found_")}
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{
                  color: "#3F5765",
                  letterSpacing: "0.05em",
                  padding: 1,
                }}
              >
                {t(
                  "typography.please_select_a_class_roster_to_fetch_details_class_requests_",
                )}
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>

      <SnackBar snack={snack} snackReset={snackReset} />
    </>
  );
}
SearchRosters.propTypes = {
  setRosterCount: PropTypes.func,
};
